import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { makeTheme, type ThemeMode } from "./theme";
import { useLanguage } from "./i18n/LanguageProvider";

interface ThemeModeCtx {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (m: ThemeMode) => void;
}

const Ctx = createContext<ThemeModeCtx>({
  mode: "light",
  toggle: () => {},
  setMode: () => {},
});

export function useThemeMode() {
  return useContext(Ctx);
}

const STORAGE_KEY = "foyer.theme-mode";

function initialMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Provides light/dark theme + a persisted toggle to the whole CMS. */
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(initialMode);
  const { muiLocale } = useLanguage();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
    // Sync native UI (scrollbars, form controls) with the chosen mode.
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const theme = useMemo(
    () => createTheme(makeTheme(mode), muiLocale),
    [mode, muiLocale]
  );
  const ctx = useMemo<ThemeModeCtx>(
    () => ({
      mode,
      setMode,
      toggle: () => setMode((m) => (m === "light" ? "dark" : "light")),
    }),
    [mode]
  );

  return (
    <Ctx.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </Ctx.Provider>
  );
}

export default ThemeModeProvider;
